import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useQuotationTasks, useQuotationTasksInvalidate } from '@/hooks/api/1440_quotation_tasks';
import { useCreateQuotationTask } from '@/hooks/api/1450_create_quotation_task';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { scrollTop } from '@/utils';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import { useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  name: string;
  description?: string;
  due_date?: string;
};

export const QuotationTasksNew = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle(`新規見積申請タスク作成`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `プロジェクト（売上） - ${_t_!.name}`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders` },
        { label: `プロジェクト（売上）詳細`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}` },
      ]),
    []
  );

  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _so_ } = useCurrentSalesOrder();
  const { mutate, data, isPending, error } = useCreateQuotationTask();
  const { data: tasks } = useQuotationTasks({
    identifier: _w_!.identifier,
    sales_order_id: _so_!.id,
    page: 1,
    per: 1000,
  });
  const { invalidate } = useQuotationTasksInvalidate(_w_!.identifier);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
    },
  });

  // タスク名を自動設定する
  useEffect(() => {
    if (tasks) {
      let _n = `見積 - ${_so_!.name}`;
      const taskCount = tasks.quotation_tasks.filter((t) => t.name.indexOf(_n) >= 0).length;
      const _suffix = taskCount == 0 ? '' : ` - ${taskCount + 1}`;
      _n = `${_n}${_suffix}`;
      setValue('name', _n);
    }
  }, [tasks]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      invalidate();
      toastSuccess('見積申請タスクの登録が完了しました');
      navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}/quotation_tasks/${data.id}`);
    }
  }, [data]);

  useEffect(() => {
    if (error) scrollTop();
  }, [error]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <WorkspaceLayout>
      <ScrollTop />

      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-1">新規見積申請タスク作成</h5>
                  <p className="text-xs mb-0">見積書の申請・管理を行うタスクを作成します。</p>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="form-group">
                    <label>プロジェクト名</label>
                    <p className="form-text text-muted text-sm ms-1">{_so_!.name}</p>
                  </div>

                  <FormGroup label="タスク名" required={true}>
                    <input
                      type="text"
                      maxLength={50}
                      className={`form-control ${errors.name && 'is-invalid'}`}
                      placeholder={'タスク名'}
                      {...register('name', {
                        required: '入力して下さい。',
                      })}
                    />
                    {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                  </FormGroup>

                  <FormGroup label="メモ">
                    <textarea
                      className="form-control"
                      maxLength={2000}
                      rows={5}
                      {...register('description')}
                    ></textarea>
                  </FormGroup>

                  <div className="row">
                    <div className="col-12 col-md-6 pe-0">
                      <FormGroup label="期限日">
                        <Controller
                          control={control}
                          name="due_date"
                          render={({ field: { ref, ...field } }) => (
                            <Flatpickr
                              className={`form-control datepickr`}
                              {...field}
                              onChange={(_, dateString) => {
                                field.onChange(dateString);
                                field.onBlur();
                              }}
                              onBlur={() => {}}
                              options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                            />
                          )}
                        />
                      </FormGroup>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>登録する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
